/**
 * Shared filename sanitization for the graph PNG and chart CSV downloads (#33).
 *
 * Returns a file *basename* only — the caller appends `.png` / `.csv`, so the
 * extension is applied exactly once and stays outside the length budget.
 *
 * The rule set is deliberately conservative: remove what is unsafe or invisible,
 * preserve everything else. Hebrew and other non-ASCII letters are kept as-is and
 * are never transliterated.
 */

/** D1: filename-unsafe characters, all replaced by a single '-'. */
const UNSAFE_CHARACTERS = /[,\/\\:*?"<>|]/g;

/**
 * Characters that must not reach a file name because they are control codes or
 * invisible: C0 controls, DEL, the LTR/RTL marks, the bidi embedding/override
 * block, and the zero-width no-break space.
 *
 * Held as explicit code-point ranges rather than a regex character class so the
 * exact set stays readable in source.
 */
const INVISIBLE_RANGES: ReadonlyArray<readonly [number, number]> = [
  [0x0000, 0x001f], // C0 control characters
  [0x007f, 0x007f], // DEL
  [0x200e, 0x200f], // LEFT-TO-RIGHT / RIGHT-TO-LEFT MARK
  [0x202a, 0x202e], // bidi embedding and override
  [0xfeff, 0xfeff], // ZERO WIDTH NO-BREAK SPACE
];

/** Windows rejects these as the stem of a file name, with or without an extension. */
const WINDOWS_RESERVED = /^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])$/i;

/** D2: maximum basename size in UTF-8 bytes, excluding the extension. */
const MAX_BASENAME_BYTES = 200;

const FALLBACK_BASENAME = 'dataset-chart';

function isInvisible(codePoint: number): boolean {
  return INVISIBLE_RANGES.some(([low, high]) => codePoint >= low && codePoint <= high);
}

function removeInvisible(value: string): string {
  return Array.from(value)
    .filter(char => !isInvisible(char.codePointAt(0)!))
    .join('');
}

function utf8Length(codePoint: number): number {
  if (codePoint <= 0x7f) return 1;
  if (codePoint <= 0x7ff) return 2;
  if (codePoint <= 0xffff) return 3;
  return 4;
}

/**
 * Strip characters that are unsafe or meaningless at either end: spaces, periods
 * (Windows silently drops trailing ones, and a leading one hides the file on
 * Unix) and the hyphens left behind by unsafe-character replacement.
 *
 * This is also what neutralizes path-traversal-like input: once '/' and '\' have
 * become '-', a leading '../../' is nothing but a run of dots and hyphens.
 */
function trimUnsafeEnds(value: string): string {
  return value.replace(/^[\s.\-]+/, '').replace(/[\s.\-]+$/, '');
}

/** Truncate to a UTF-8 byte budget, never splitting a code point. */
function truncateToUtf8Bytes(value: string, maxBytes: number): string {
  let bytes = 0;
  let result = '';

  // Array.from iterates by code point, so a surrogate pair is never severed.
  for (const char of Array.from(value)) {
    const size = utf8Length(char.codePointAt(0)!);
    if (bytes + size > maxBytes) break;
    bytes += size;
    result += char;
  }

  return result;
}

export function toSafeFileName(name: string): string {
  let cleaned = removeInvisible(name || '')
    .replace(UNSAFE_CHARACTERS, '-')
    .replace(/\s+/g, ' ')
    .replace(/-{2,}/g, '-');

  cleaned = trimUnsafeEnds(cleaned);
  cleaned = trimUnsafeEnds(truncateToUtf8Bytes(cleaned, MAX_BASENAME_BYTES));

  if (!cleaned) {
    return FALLBACK_BASENAME;
  }

  // Windows treats the text before the first period as the reserved name, so
  // 'CON' and 'CON.txt' are both refused.
  if (WINDOWS_RESERVED.test(cleaned.split('.')[0])) {
    // Make room for the '_' before adding it. Prefixing afterwards would push a
    // name that already sits exactly on the budget one byte over it.
    const guarded = trimUnsafeEnds(truncateToUtf8Bytes(cleaned, MAX_BASENAME_BYTES - 1));
    return guarded ? `_${guarded}` : FALLBACK_BASENAME;
  }

  return cleaned;
}
